<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import ChatSection from './ChatSection.vue';
import OutputSection from './OutputSection.vue';
import PreviewSection from './PreviewSection.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const uiFlags = useMapGetter('wgptBotKnowledgeManager/getUIFlags');
const botId = computed(() => Number(route.params.botId));
const isLoading = computed(() => uiFlags.value.isFetching);
const previewVersion = ref(null);

const RESPONSE_TIMEOUT = 120000;
const ERROR_MESSAGES = {
  failed: 'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.ERROR_TOAST',
  timeout: 'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.TIMEOUT_TOAST',
  processing:
    'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.PROCESSING_TOAST',
};

let responseTimer = null;
const clearResponseTimer = () => {
  if (responseTimer) {
    clearTimeout(responseTimer);
    responseTimer = null;
  }
};

// Client-side fallback: if no completed/failed event arrives, stop waiting.
watch(
  () => uiFlags.value.isSending,
  isSending => {
    clearResponseTimer();
    if (isSending) {
      responseTimer = setTimeout(() => {
        store.dispatch('wgptBotKnowledgeManager/handleTimeout');
      }, RESPONSE_TIMEOUT);
    }
  }
);

// Surface failures/timeouts/conflicts as a translated toast, then reset.
watch(
  () => uiFlags.value.error,
  error => {
    if (!error) return;
    useAlert(t(ERROR_MESSAGES[error] || ERROR_MESSAGES.failed));
    store.dispatch('wgptBotKnowledgeManager/clearError');
  }
);

// On websocket reconnect mid-run, rebuild from Redis (the source of truth)
// in case the completed/failed broadcast was missed during the disconnect.
const onReconnect = () => {
  if (uiFlags.value.isSending) {
    store.dispatch('wgptBotKnowledgeManager/resync', botId.value);
  }
};

const togglePreview = data => {
  if (previewVersion.value?.id === data.id) {
    previewVersion.value = null;
  } else {
    previewVersion.value = data;
  }
};

onMounted(() => {
  store.dispatch('wgptBotKnowledgeManager/get', botId.value);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect);
});

onBeforeUnmount(() => {
  clearResponseTimer();
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect);
});
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center h-full w-full">
    <SpinnerLoader :size="28" class="text-n-blue-9" />
  </div>
  <div v-else class="h-full flex">
    <PreviewSection
      v-if="previewVersion"
      class="w-3/5 min-w-0"
      :preview="previewVersion"
      @exit-preview="previewVersion = null"
    />
    <OutputSection v-else class="w-3/5 min-w-0" />
    <div class="w-px h-full bg-n-weak/60" />
    <ChatSection
      class="w-2/5 min-w-0"
      :preview-version="previewVersion"
      @toggle-preview="togglePreview"
    />
  </div>
</template>
